(() => {
  const CONTENT_SELECTOR = '.article-template__content';
  const CARD_SELECTOR = '.pathosion-article-link-card';
  const ARTICLE_PATH_PATTERN =
    /^\/(?:([a-z]{2,3}(?:-[a-z0-9]{2,8})*)\/)?blogs\/([^/]+)\/([^/]+)\/?$/i;
  const metadataByPath = new Map();
  const metadataRequests = new Map();

  const normalizeHost = (host) => host.toLowerCase().replace(/^www\./, '');
  const getArticlePathKey = (path) => {
    const match = path.match(ARTICLE_PATH_PATTERN);
    if (!match) return '';

    return `${(match[1] || '').toLowerCase()}|${match[2]}|${match[3]}`;
  };
  const toPlainText = (value) => {
    if (typeof value !== 'string') return '';

    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value.replace(/\s+/g, ' ').trim();
  };

  const canonicalUrl = (() => {
    const canonicalLink = document.querySelector('link[rel="canonical"]');

    try {
      return new URL(canonicalLink?.href || window.location.href);
    } catch (_error) {
      return new URL(window.location.href);
    }
  })();

  const allowedHosts = new Set(
    [window.location.hostname, canonicalUrl.hostname, window.Shopify?.shop]
      .filter(Boolean)
      .map(normalizeHost)
  );

  const resolveArticleUrl = (value) => {
    try {
      const url = new URL(value, window.location.href);

      if (!['http:', 'https:'].includes(url.protocol)) return null;
      if (!allowedHosts.has(normalizeHost(url.hostname))) return null;
      if (!ARTICLE_PATH_PATTERN.test(url.pathname)) return null;

      return url;
    } catch (_error) {
      return null;
    }
  };

  const readMetadataIndex = () => {
    document.querySelectorAll('[data-pathosion-article-link-card-index]').forEach((element) => {
      try {
        const entries = JSON.parse(element.textContent);

        entries.forEach((entry) => {
          const url = resolveArticleUrl(entry.url);

          if (!url) return;
          metadataByPath.set(getArticlePathKey(url.pathname), {
            ...entry,
            description: toPlainText(entry.description),
          });
        });
      } catch (_error) {
        // A malformed optional index should not affect the article body.
      }
    });
  };

  const getSchemaType = (value) => {
    if (Array.isArray(value)) return value;
    return value ? [value] : [];
  };

  const findArticleSchema = (documentNode) => {
    const schemas = documentNode.querySelectorAll('script[type="application/ld+json"]');

    for (const schemaElement of schemas) {
      try {
        const queue = [JSON.parse(schemaElement.textContent)];

        while (queue.length > 0) {
          const value = queue.shift();

          if (Array.isArray(value)) {
            queue.push(...value);
            continue;
          }

          if (!value || typeof value !== 'object') continue;

          const types = getSchemaType(value['@type']);
          if (types.some((type) => ['Article', 'BlogPosting', 'NewsArticle'].includes(type))) {
            return value;
          }

          Object.values(value).forEach((child) => {
            if (child && typeof child === 'object') queue.push(child);
          });
        }
      } catch (_error) {
        // Continue to the next structured-data block.
      }
    }

    return null;
  };

  const getName = (value) => {
    if (typeof value === 'string') return value.trim();
    if (Array.isArray(value)) return getName(value[0]);
    return typeof value?.name === 'string' ? value.name.trim() : '';
  };

  const normalizePublisher = (value) => value.replace(/^株式会社/, '').trim();

  const parseMetadata = (html) => {
    const documentNode = new DOMParser().parseFromString(html, 'text/html');
    const articleSchema = findArticleSchema(documentNode);
    const getMeta = (selector) => documentNode.querySelector(selector)?.content?.trim() || '';

    return {
      title:
        getMeta('meta[property="og:title"]') ||
        articleSchema?.headline?.trim() ||
        documentNode.querySelector('h1')?.textContent?.trim() ||
        '',
      description:
        getMeta('meta[property="og:description"]') ||
        articleSchema?.description?.trim() ||
        getMeta('meta[name="description"]'),
      image: getMeta('meta[property="og:image:secure_url"]') || getMeta('meta[property="og:image"]'),
      imageAlt: getMeta('meta[property="og:image:alt"]'),
      author: getName(articleSchema?.author),
      publisher: normalizePublisher(getName(articleSchema?.publisher) || getMeta('meta[property="og:site_name"]')),
    };
  };

  const fetchMetadata = async (articleUrl) => {
    const requestUrl = new URL(articleUrl.pathname + articleUrl.search, window.location.origin);
    const response = await fetch(requestUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    });

    if (!response.ok) throw new Error(`Article request failed with ${response.status}`);

    const metadata = parseMetadata(await response.text());
    if (!metadata.title || !metadata.image) throw new Error('Article metadata is incomplete');

    return metadata;
  };

  const getMetadata = (articleUrl) => {
    const key = getArticlePathKey(articleUrl.pathname);
    const indexedMetadata = metadataByPath.get(key);

    if (indexedMetadata?.title && indexedMetadata?.image) {
      return Promise.resolve(indexedMetadata);
    }

    if (!metadataRequests.has(key)) {
      metadataRequests.set(
        key,
        fetchMetadata(articleUrl)
          .then((metadata) => {
            metadataByPath.set(key, metadata);
            return metadata;
          })
          .catch((error) => {
            metadataRequests.delete(key);
            throw error;
          })
      );
    }

    return metadataRequests.get(key);
  };

  const isOnlyChildContent = (container, anchor) =>
    [...container.childNodes].every((node) => {
      if (node === anchor) return true;
      if (node.nodeType === Node.TEXT_NODE) return node.textContent.trim() === '';
      return node.nodeType === Node.ELEMENT_NODE && node.tagName === 'BR';
    });

  const collectCandidates = (content) => {
    const candidates = [];
    const handledSources = new Set();

    content.querySelectorAll('a[href]').forEach((anchor) => {
      if (anchor.closest(CARD_SELECTOR)) return;

      const source = anchor.parentElement;
      if (!source?.matches('p') || !isOnlyChildContent(source, anchor)) return;

      const articleUrl = resolveArticleUrl(anchor.href);
      if (!articleUrl || getArticlePathKey(articleUrl.pathname) === getArticlePathKey(canonicalUrl.pathname)) return;

      handledSources.add(source);
      candidates.push({
        source,
        articleUrl,
        target: anchor.target,
        rel: anchor.rel,
      });
    });

    content.querySelectorAll('p').forEach((source) => {
      if (handledSources.has(source) || source.closest(CARD_SELECTOR)) return;
      if ([...source.children].some((child) => child.tagName !== 'BR')) return;

      const articleUrl = resolveArticleUrl(source.textContent.trim());
      if (!articleUrl || getArticlePathKey(articleUrl.pathname) === getArticlePathKey(canonicalUrl.pathname)) return;

      candidates.push({ source, articleUrl, target: '', rel: '' });
    });

    return candidates;
  };

  const createElement = (tagName, className, text) => {
    const element = document.createElement(tagName);

    if (className) element.className = className;
    if (text) element.textContent = text;
    return element;
  };

  const createCard = ({ articleUrl, target, rel }, metadata) => {
    const wrapper = createElement(
      'div',
      'pathosion-article-link-card pathosion-article-link-card--inline'
    );
    const link = createElement('a', 'pathosion-article-link-card__card');
    const body = createElement('div', 'pathosion-article-link-card__body');
    const title = createElement('p', 'pathosion-article-link-card__title');
    const titleText = createElement('span', '', metadata.title);

    wrapper.dataset.articleLinkCardAuto = 'true';
    link.href = articleUrl.href;

    if (target) link.target = target;
    if (rel) link.rel = rel;
    if (target === '_blank' && !rel) link.rel = 'noopener noreferrer';

    title.appendChild(titleText);

    if (metadata.author || metadata.publisher) {
      const byline = createElement('span', 'pathosion-article-link-card__byline');

      if (metadata.author) {
        byline.appendChild(
          createElement('span', 'pathosion-article-link-card__source', metadata.author)
        );
      }

      if (metadata.publisher && metadata.publisher !== metadata.author) {
        byline.appendChild(
          createElement(
            'span',
            'pathosion-article-link-card__source pathosion-article-link-card__source--publisher',
            metadata.publisher
          )
        );
      }

      title.appendChild(byline);
    }

    body.appendChild(title);

    if (metadata.description) {
      body.appendChild(
        createElement('p', 'pathosion-article-link-card__description', metadata.description)
      );
    }

    body.appendChild(
      createElement(
        'p',
        'pathosion-article-link-card__domain',
        articleUrl.hostname.replace(/^www\./, '')
      )
    );

    const media = createElement('div', 'pathosion-article-link-card__media');
    const image = document.createElement('img');

    image.src = metadata.image;
    image.alt = metadata.imageAlt || metadata.title;
    image.loading = 'lazy';
    image.decoding = 'async';
    media.appendChild(image);

    link.append(body, media);
    wrapper.appendChild(link);
    return wrapper;
  };

  const enhanceCandidate = async (candidate) => {
    if (candidate.source.dataset.articleLinkCardPending === 'true') return false;

    candidate.source.dataset.articleLinkCardPending = 'true';

    try {
      const metadata = await getMetadata(candidate.articleUrl);

      if (!candidate.source.isConnected) return false;
      candidate.source.replaceWith(createCard(candidate, metadata));
      return true;
    } catch (_error) {
      delete candidate.source.dataset.articleLinkCardPending;
      return false;
    }
  };

  const enhance = async (root = document) => {
    readMetadataIndex();

    const contents = root.matches?.(CONTENT_SELECTOR)
      ? [root]
      : [...root.querySelectorAll(CONTENT_SELECTOR)];
    const candidates = contents.flatMap(collectCandidates);
    const results = await Promise.all(candidates.map(enhanceCandidate));
    const convertedCount = results.filter(Boolean).length;

    document.dispatchEvent(
      new CustomEvent('pathosion:article-link-cards:ready', {
        detail: { convertedCount },
      })
    );

    return convertedCount;
  };

  window.PathosionArticleLinkCards = { enhance };
  enhance();
})();
